// =============================================================================
// INDIVIDUAL PROJECT PAGE COMPONENT
// =============================================================================

import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { SEOHead } from '@/components/SEO';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { getProjectBySlug } from '@/data/portfolioContent';
import { navItems, footerLinks, socialLinks } from '@/data/homeContent';

export const ProjectPage: React.FC = () => {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  
  if (!projectSlug) {
    return <Navigate to="/portfolio" replace />;
  }

  const project = getProjectBySlug(projectSlug);
  
  if (!project) {
    return <Navigate to="/portfolio" replace />;
  }

  return (
    <div className="project-page">
      <SEOHead
        title={project.seo.title}
        description={project.seo.description}
        keywords={project.seo.keywords}
        canonicalUrl={project.seo.canonicalUrl}
        ogImage={`https://alexfinger.com${project.image}`}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": project.title,
            "description": project.description,
            "url": project.url,
            "publisher": {
              "@type": "Organization",
              "name": "Alex Finger Consulting"
            },
            "dateCreated": project.launchDate,
            "inLanguage": "en-US"
          }
        ]}
      />
      
      <Navbar 
        brand="Alex Finger Consulting"
        navItems={navItems}
        showThemeSwitcher={false}
      />
      
      <main className="main-content">
        {/* Hero Section */}
        <section className="project-hero">
          <div className="container">
            <div className="project-hero-content">
              <div className="project-hero-text">
                <h1 className="project-title">{project.title}</h1>
                <p className="project-subtitle">{project.subtitle}</p>
                <p className="project-description">{project.description}</p>
                <div className="project-actions">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => window.open(project.url, '_blank', 'noopener,noreferrer')}
                  >
                    Visit Live Site
                  </Button>
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => window.location.href = '/portfolio'}
                  >
                    Back to Portfolio
                  </Button>
                </div>
              </div>
              <div className="project-hero-image">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="project-image"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Project Details */}
        <section className="project-details">
          <div className="container">
            <div className="project-details-grid">
              <div className="project-content">
                <div 
                  className="project-long-description"
                  dangerouslySetInnerHTML={{ __html: project.longDescription }}
                />
              </div>
              
              <div className="project-sidebar">
                <Card className="project-info-card">
                  <h3>Project Information</h3>
                  <div className="project-info-item">
                    <strong>Status:</strong>
                    <span className="project-status">{project.status}</span>
                  </div>
                  <div className="project-info-item">
                    <strong>Launch Date:</strong>
                    <span>{project.launchDate}</span>
                  </div>
                  <div className="project-info-item">
                    <strong>Technologies:</strong>
                    <div className="project-technologies">
                      {project.technologies.map((tech, index) => (
                        <span key={index} className="tech-tag">{tech}</span>
                      ))}
                    </div>
                  </div>
                  <div className="project-info-item">
                    <strong>Live URL:</strong>
                    <a 
                      href={project.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="project-url"
                    >
                      {project.url}
                    </a>
                  </div>
                </Card>

                {project.metrics && (
                  <Card className="project-metrics-card">
                    <h3>Project Metrics</h3>
                    {project.metrics.growth && (
                      <div className="metric-item">
                        <strong>Growth:</strong>
                        <span>{project.metrics.growth}</span>
                      </div>
                    )}
                    {project.metrics.users && (
                      <div className="metric-item">
                        <strong>Users:</strong>
                        <span>{project.metrics.users}</span>
                      </div>
                    )}
                    {project.metrics.performance && (
                      <div className="metric-item">
                        <strong>Performance:</strong>
                        <span>{project.metrics.performance}</span>
                      </div>
                    )}
                  </Card>
                )}

                {project.testimonial && (
                  <Card className="project-testimonial-card">
                    <h3>Client Testimonial</h3>
                    <blockquote className="testimonial-quote">
                      "{project.testimonial.quote}"
                    </blockquote>
                    <cite className="testimonial-author">
                      ;  {project.testimonial.author}
                      {project.testimonial.role && (
                        <span className="testimonial-role">, {project.testimonial.role}</span>
                      )}
                    </cite>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="project-features">
          <div className="container">
            <h2>Key Features</h2>
            <div className="features-grid">
              {project.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <div className="feature-icon">✓</div>
                  <span className="feature-text">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="project-cta">
          <div className="container">
            <Card className="cta-card">
              <h2>Ready to Start Your Project?</h2>
              <p>Let's discuss how we can transform your idea into a continuously evolving digital success story.</p>
              <div className="cta-actions">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => window.location.href = '/contact'}
                >
                  Start Your Project
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => window.location.href = '/services'}
                >
                  Learn About Our Process
                </Button>
              </div>
            </Card>
          </div>
        </section>
      </main>
      
      <Footer
        brand="Alex Finger Consulting"
        description="Web development and business strategy services. Transform your ideas into digital reality."
        links={footerLinks}
        socialLinks={socialLinks}
      />
    </div>
  );
};
